import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useT } from '../i18n/LanguageContext';
import { TYPE_KEYS, BADGE_KEYS } from '../i18n/translations';
import garrafaTinto  from '../assets/garrafa_card_tinto.png';
import garrafaBranco from '../assets/garrafa_card_branco.png';

/**
 * WineCard — Cartão de produto para vinhos
 *
 * Props:
 *   id          {number|string} — Identificador único (obrigatório p/ carrinho)
 *   image       {string}  — URL da imagem (usa placeholder se omitido)
 *   badge       {string}  — Texto do crachá (ex: "Novo", "Edição Limitada")
 *   type        {string}  — Tipo de vinho (ex: "Tinto", "Branco", "Rosé")
 *   name        {string}  — Nome do vinho
 *   year        {number}  — Colheita
 *   description {string}  — Descrição curta
 *   price       {string}  — Preço (ex: "€18,50")
 *   dark        {boolean} — Versão escura do cartão
 *   showActions {boolean} — Mostrar botões comprar/adicionar (default true)
 */
export default function WineCard({
  id,
  image,
  badge,
  type        = 'Tinto',
  name        = 'Nome do Vinho',
  year        = new Date().getFullYear(),
  description = 'Notas de frutos vermelhos maduros, taninos sedosos e final longo e persistente.',
  price       = '€00,00',
  dark        = false,
  showActions = true,
  unavailable = false,
}) {
  const { addItem, items } = useCart();
  const navigate = useNavigate();
  const t = useT();

  // Quantidade já no carrinho para este vinho
  const cartItem = items.find(i => String(i.id) === String(id));
  const qtyInCart = cartItem ? cartItem.qty : 0;

  // Escolhe imagem da garrafa pelo tipo (tinto vs branco) caso não venha por prop
  const isBranco = /branco/i.test(type);
  const cardImage = image || (isBranco ? garrafaBranco : garrafaTinto);

  const product = { id, image: cardImage, badge, type, name, year, description, price };

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (unavailable) return;
    addItem(product, 1);
  };

  const handleBuy = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (unavailable) return;
    addItem(product, 1);
    navigate('/carrinho');
  };

  // Traduções (com fallback ao valor original se não houver chave mapeada)
  const typeLabel  = TYPE_KEYS[type]   ? t(TYPE_KEYS[type])   : type;
  const badgeLabel = badge && BADGE_KEYS[badge] ? t(BADGE_KEYS[badge]) : badge;
  const descLabel  = description;

  return (
    <article className={`card${dark ? ' card--dark' : ''}${unavailable ? ' card--unavailable' : ''}`}>
      <div className="card__image-wrap">
        <img src={cardImage} alt={`${name} ${year}`} className="card__image" />

        {/* Overlay "Em breve" para vinhos indisponíveis */}
        {unavailable && (
          <div className="card__unavailable-overlay">
            <span className="card__unavailable-label">Em breve</span>
          </div>
        )}

        {/* Contador de quantidade no carrinho */}
        {!unavailable && qtyInCart > 0 && (
          <div className="card__cart-qty">
            {qtyInCart} no carrinho
          </div>
        )}

        {/* Bloco de fallback abaixo nunca é usado porque cardImage tem
            sempre um valor (tinto ou branco). Mantido apenas como
            referência caso o sistema de imagens mude. */}
        {false && (
          /* ── Placeholder enquanto não há imagem ── */
          <div
            className="card__image"
            style={{
              aspectRatio: '3/4',
              background: dark
                ? 'linear-gradient(160deg, var(--clr-terra), var(--clr-noir))'
                : 'linear-gradient(160deg, var(--clr-areia), var(--clr-cinza-claro))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 'var(--sp-2)',
            }}
          >
            <svg width="32" height="64" viewBox="0 0 32 64" fill="none" aria-hidden="true">
              <path
                d="M16 2 C8 10 4 20 4 28 C4 40 10 48 16 50 C22 48 28 40 28 28 C28 20 24 10 16 2Z"
                stroke={dark ? 'rgba(240,232,214,0.3)' : 'rgba(44,21,5,0.2)'}
                strokeWidth="1"
                fill="none"
              />
              <line
                x1="16" y1="50" x2="16" y2="62"
                stroke={dark ? 'rgba(240,232,214,0.3)' : 'rgba(44,21,5,0.2)'}
                strokeWidth="1"
              />
              <line
                x1="10" y1="62" x2="22" y2="62"
                stroke={dark ? 'rgba(240,232,214,0.3)' : 'rgba(44,21,5,0.2)'}
                strokeWidth="1"
              />
            </svg>
            <span style={{
              fontSize: 'var(--fz-xs)',
              letterSpacing: 'var(--ls-wide)',
              textTransform: 'uppercase',
              opacity: 0.4,
              color: dark ? 'var(--clr-areia)' : 'var(--clr-terra)',
            }}>
              {t('card.imagePlaceholder')}
            </span>
          </div>
        )}

        {badge && <span className="card__badge">{badgeLabel}</span>}
      </div>

      <div className="card__body">
        <p className="card__eyebrow">{typeLabel} · {year}</p>
        <h3 className="card__title">{name}</h3>
        <p className="card__text">{descLabel}</p>

        <div className="card__price-row">
          <span className="card__price">{price}</span>
        </div>

        {showActions && !unavailable && (
          <div className="card__actions">
            <button
              type="button"
              onClick={handleAdd}
              className={`btn btn-sm${dark ? ' btn--on-dark' : ''} card__btn`}
              aria-label={t('card.addAria', { name })}
            >
              {t('card.add')}
            </button>
            <button
              type="button"
              onClick={handleBuy}
              className={`btn btn-sm${dark ? ' btn--on-dark' : ''} card__btn`}
              aria-label={t('card.buyAria', { name })}
            >
              {t('card.buy')}
            </button>
          </div>
        )}
      </div>
    </article>
  );
}
